import { useNavigate } from "react-router-dom";
import { useCustom, useCustomMutation } from "@refinedev/core";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  TimePicker,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import { apiUrlV2 } from "@/consts";
import { wallClockISO } from "../scSchedule/time";

const { Title, Text } = Typography;

// GL-000 DCC: platform-authored fitness classes.
//
// Deliberately NOT a copy of the S&C form with a different label. Two real
// differences:
//   • No target. Classes are owner-wide; the audience is resolved from the
//     member (fitness track + programme membership + current consent), so there
//     is no group to pick.
//   • A category instead (yoga, pilates, …), drawn from the platform's existing
//     admin-managed category list.
//
// Check-ins are also absent, not hidden by accident — see the note in the form.

const WEEKDAYS = [
  { value: "MON", label: "Mon" },
  { value: "TUE", label: "Tue" },
  { value: "WED", label: "Wed" },
  { value: "THU", label: "Thu" },
  { value: "FRI", label: "Fri" },
  { value: "SAT", label: "Sat" },
  { value: "SUN", label: "Sun" },
];

type CategoryRow = { id: string; title: string; status?: string };

export function useCategoryOptions() {
  // GET /v1/category answers `{ list, page, pageSize, total }` (src/category/index.ts).
  const { data, isLoading } = useCustom<{
    list: CategoryRow[];
    page: number;
    pageSize: number;
    total: number;
  }>({
    url: `${apiUrlV2}/category`,
    method: "get",
    config: { query: { status: "ACTIVE", _start: 0, _end: 1000 } },
  });

  const rows = data?.data.list ?? [];

  return {
    isLoading,
    options: rows.map((r) => ({ value: r.id, label: r.title })),
  };
}

export function FitnessScheduleCreate() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const mode = Form.useWatch("mode", form) ?? "recurring";
  const freq = Form.useWatch("freq", form) ?? "WEEKLY";
  const endsType = Form.useWatch("endsType", form) ?? "AFTER_WEEKS";

  const { options: categoryOptions, isLoading: categoriesLoading } = useCategoryOptions();
  const { mutate, isLoading } = useCustomMutation();

  function handleFinish(values: any) {
    const base: Record<string, unknown> = {
      type: "FITNESS",
      title: values.title?.trim() || undefined,
      notes: values.notes?.trim() || undefined,
      categoryIDs: values.categoryIDs ?? [],
      // No `target`: the API rejects one for a fitness class rather than
      // silently ignoring it.
    };

    let payload: Record<string, unknown>;
    if (values.mode === "one_off") {
      payload = {
        ...base,
        startsAt: wallClockISO(values.startsAt as dayjs.Dayjs),
        endsAt: wallClockISO(values.endsAt as dayjs.Dayjs),
      };
    } else {
      const recurrence: Record<string, unknown> = {
        freq: values.freq,
        startDate: (values.startDate as dayjs.Dayjs).format("YYYY-MM-DD"),
        endsType: values.endsType,
        timeOfDay: (values.timeOfDay as dayjs.Dayjs).format("HH:mm"),
        endTimeOfDay: (values.endTimeOfDay as dayjs.Dayjs).format("HH:mm"),
      };
      if (values.freq === "WEEKLY" || values.freq === "BIWEEKLY") {
        recurrence.byDays = values.byDays;
      }
      if (values.endsType === "ON_DATE") {
        recurrence.endsOn = (values.endsOn as dayjs.Dayjs).format("YYYY-MM-DD");
      }
      if (values.endsType === "AFTER_WEEKS") {
        recurrence.endsAfterWeeks = values.endsAfterWeeks;
      }
      payload = { ...base, recurrence };
    }

    mutate(
      { url: `${apiUrlV2}/schedule/admin/fitness`, method: "post", values: payload },
      {
        onSuccess: () => {
          message.success("Fitness class created");
          navigate("/fitness-schedule");
        },
        onError: (err: any) => {
          message.error(err?.response?.data?.error || err?.message || "Could not create class");
        },
      },
    );
  }

  return (
    <Card style={{ maxWidth: 720, margin: "0 auto" }}>
      <Title level={4}>Create fitness class</Title>
      <Text type="secondary">
        A platform-wide class. Every programme member on the fitness track sees it —
        there is no group or district to pick.
      </Text>

      <Form
        form={form}
        layout="vertical"
        style={{ marginTop: 24 }}
        initialValues={{ mode: "recurring", freq: "WEEKLY", endsType: "AFTER_WEEKS" }}
        onFinish={handleFinish}
      >
        <Form.Item
          name="categoryIDs"
          label="Category"
          tooltip="Yoga, pilates, and so on. Categories describe and group classes — they do not affect who can join."
        >
          <Select
            mode="multiple"
            allowClear
            loading={categoriesLoading}
            options={categoryOptions}
            placeholder="Select one or more categories"
          />
        </Form.Item>

        <Form.Item name="title" label="Title (optional)">
          <Input placeholder="e.g. Morning Yoga" maxLength={255} />
        </Form.Item>

        <Form.Item name="mode" label="Type">
          <Radio.Group
            options={[
              { value: "recurring", label: "Recurring" },
              { value: "one_off", label: "One-off" },
            ]}
            optionType="button"
          />
        </Form.Item>

        {mode === "one_off" ? (
          <Space size="large" wrap>
            <Form.Item
              name="startsAt"
              label="Starts at"
              rules={[{ required: true, message: "Pick a start" }]}
            >
              <DatePicker showTime format="YYYY-MM-DD HH:mm" />
            </Form.Item>
            <Form.Item
              name="endsAt"
              label="Ends at"
              rules={[{ required: true, message: "Pick an end" }]}
            >
              <DatePicker showTime format="YYYY-MM-DD HH:mm" />
            </Form.Item>
          </Space>
        ) : (
          <>
            <Space size="large" wrap>
              <Form.Item name="freq" label="Frequency" rules={[{ required: true }]}>
                <Select
                  style={{ width: 160 }}
                  options={[
                    { value: "WEEKLY", label: "Weekly" },
                    { value: "BIWEEKLY", label: "Every 2 weeks" },
                    { value: "MONTHLY", label: "Monthly" },
                  ]}
                />
              </Form.Item>

              <Form.Item
                name="startDate"
                label="Start date"
                rules={[{ required: true, message: "Pick a start date" }]}
              >
                <DatePicker format="YYYY-MM-DD" />
              </Form.Item>
            </Space>

            {(freq === "WEEKLY" || freq === "BIWEEKLY") && (
              <Form.Item
                name="byDays"
                label="On days"
                rules={[{ required: true, message: "Pick at least one day" }]}
              >
                <Select mode="multiple" options={WEEKDAYS} placeholder="Select days" />
              </Form.Item>
            )}

            <Space size="large" wrap>
              <Form.Item
                name="timeOfDay"
                label="Start time"
                rules={[{ required: true, message: "Pick a start time" }]}
              >
                <TimePicker format="HH:mm" minuteStep={5} />
              </Form.Item>
              <Form.Item
                name="endTimeOfDay"
                label="End time"
                rules={[{ required: true, message: "Pick an end time" }]}
              >
                <TimePicker format="HH:mm" minuteStep={5} />
              </Form.Item>
            </Space>

            <Form.Item name="endsType" label="Ends" rules={[{ required: true }]}>
              <Select
                style={{ width: 220 }}
                options={[
                  { value: "AFTER_WEEKS", label: "After N weeks" },
                  { value: "ON_DATE", label: "On a date" },
                  { value: "END_OF_SEASON", label: "End of season" },
                ]}
              />
            </Form.Item>

            {endsType === "AFTER_WEEKS" && (
              <Form.Item
                name="endsAfterWeeks"
                label="Number of weeks"
                rules={[{ required: true, message: "Enter the number of weeks" }]}
              >
                <InputNumber min={1} max={104} />
              </Form.Item>
            )}
            {endsType === "ON_DATE" && (
              <Form.Item
                name="endsOn"
                label="End date"
                rules={[{ required: true, message: "Pick an end date" }]}
              >
                <DatePicker format="YYYY-MM-DD" />
              </Form.Item>
            )}
          </>
        )}

        <Form.Item name="notes" label="Notes (optional)">
          <Input.TextArea rows={3} placeholder="Internal notes for this class" />
        </Form.Item>

        {/* Not an oversight: check-ins resolve through a session's roster, and a
            class has no roster because it is owner-wide. Offering the toggle
            would let an admin switch on something that silently does nothing. */}
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
          message="Attendance check-ins are not available for fitness classes"
          description="Check-ins are tied to a session's roster, and classes are open to every programme member rather than a fixed group."
        />

        <Space>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            Create class
          </Button>
          <Button onClick={() => navigate("/fitness-schedule")}>Cancel</Button>
        </Space>
      </Form>
    </Card>
  );
}
